'use client';

import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import {
  toasterApi,
  type InternalEnrichedFinding,
} from '@terra/ui-design-system';
import { terraProxyApiClient } from '../../../clients/testerClient';
import { useMainContentTabs } from '../../../store/layoutStore';
import { ENTITY_TAB_TYPE } from '../../../store/layoutStoreTypes';
import { invalidateSignalQueriesAfterFindingStatusChange } from '../../../hooks/api/invalidateSignalQueriesAfterFindingStatusChange';
import { useInvalidateFindingById } from '../../../hooks/api/useFindings';
import {
  FINDING_DELETE_SIGNAL_ACTION,
  didBulkDeleteIncludeSignalIds,
  resolveStageForFindingDeleteStatusUpdate,
  toFindingDeleteRelatedSignal,
  type FindingDeleteRelatedSignal,
  type FindingDeleteSignalDisposition,
} from '../findingDeleteSignalDisposition';
import type { FindingsSelectionModel } from './useFindingsSelection';
import { useFindingsBulkConflictToast } from './useFindingsBulkConflictToast';

export function useFindingsDeleteAction({
  selectionModel,
  onMutated,
}: {
  selectionModel: Pick<
    FindingsSelectionModel,
    'selection' | 'clearSelection' | 'buildBulkFilterPayload'
  >;
  onMutated: () => Promise<void>;
}) {
  const t = useTranslations('findingsTab.deleteConfirmation');
  const tCopilotTabTitles = useTranslations('chat.copilot.tabTitles');
  const queryClient = useQueryClient();
  const invalidateFindingById = useInvalidateFindingById();
  const { openInNewTab } = useMainContentTabs();
  const toastBulkConflict = useFindingsBulkConflictToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingFindingId, setDeletingFindingId] = useState<string | null>(
    null,
  );
  const [relatedSignal, setRelatedSignal] =
    useState<FindingDeleteRelatedSignal | null>(null);
  const { selection, clearSelection, buildBulkFilterPayload } = selectionModel;

  const requestDeleteFinding = useCallback(
    (finding: InternalEnrichedFinding) => {
      setDeletingFindingId(finding.id);
      setRelatedSignal(
        toFindingDeleteRelatedSignal({
          signalSummary: finding.signalSummary,
          signalId: finding.signalId,
        }),
      );
      setIsOpen(true);
    },
    [],
  );
  const requestBulkDelete = useCallback(() => {
    setDeletingFindingId(null);
    setRelatedSignal(null);
    setIsOpen(true);
  }, []);
  const closeDialog = useCallback(() => {
    setIsOpen(false);
    setDeletingFindingId(null);
    setRelatedSignal(null);
  }, []);
  const applyRelatedSignalDisposition = useCallback(
    async ({
      signal,
      disposition,
    }: {
      signal: FindingDeleteRelatedSignal;
      disposition: FindingDeleteSignalDisposition;
    }): Promise<boolean> => {
      try {
        if (disposition.action === FINDING_DELETE_SIGNAL_ACTION.DELETE) {
          const result = await terraProxyApiClient.deleteSignalsBulk([
            signal.id,
          ]);

          if (
            !didBulkDeleteIncludeSignalIds({ result, expectedIds: [signal.id] })
          ) {
            throw new Error(t('signalDeleteFailedDescription'));
          }

          return true;
        }

        if (disposition.action === FINDING_DELETE_SIGNAL_ACTION.CHANGE_STATUS) {
          await terraProxyApiClient.updateSignal(signal.id, {
            stage: resolveStageForFindingDeleteStatusUpdate({
              currentStage: signal.stage,
              newStatus: disposition.status,
            }),
            status: disposition.status,
          });
        }

        return true;
      } catch (signalError) {
        const isDelete =
          disposition.action === FINDING_DELETE_SIGNAL_ACTION.DELETE;

        console.error(
          'Failed to apply related signal disposition after finding delete:',
          signalError,
        );
        toasterApi.error({
          title: isDelete
            ? t('signalDeleteFailedTitle')
            : t('signalUpdateFailedTitle'),
          description:
            signalError instanceof Error
              ? signalError.message
              : isDelete
                ? t('signalDeleteFailedDescription')
                : t('signalUpdateFailedDescription'),
        });

        return false;
      }
    },
    [t],
  );
  const deleteSingle = useCallback(
    async ({
      findingId,
      disposition,
    }: {
      findingId: string;
      disposition: FindingDeleteSignalDisposition | null;
    }): Promise<{ toastedFailure: boolean; changedSignal: boolean }> => {
      const response = await terraProxyApiClient.deleteFinding(findingId);

      if (response.status !== 200) {
        throw new Error(t('errorDescription'));
      }

      const changesSignal =
        disposition !== null &&
        disposition.action !== FINDING_DELETE_SIGNAL_ACTION.KEEP &&
        relatedSignal !== null;
      const didApplyDisposition =
        changesSignal && relatedSignal
          ? await applyRelatedSignalDisposition({
              signal: relatedSignal,
              disposition,
            })
          : true;

      if (didApplyDisposition) {
        toasterApi.success({
          title: t('successTitle'),
          description: t('successDescription'),
        });
      }

      return {
        toastedFailure: !didApplyDisposition,
        changedSignal: changesSignal && didApplyDisposition,
      };
    },
    [applyRelatedSignalDisposition, relatedSignal, t],
  );
  const deleteSelection = useCallback(async (): Promise<boolean> => {
    if (selection.mode === 'ids' && selection.ids.size === 0) {
      toasterApi.error({
        title: t('noSelectionTitle'),
        description: t('noSelectionDescription'),
      });

      return false;
    }

    const response =
      selection.mode === 'ids'
        ? await terraProxyApiClient.deleteFindingsBulk(
            Array.from(selection.ids),
          )
        : await terraProxyApiClient.deleteFindingsBulkByFilter(
            buildBulkFilterPayload(),
          );

    if (response.status === 200) {
      toasterApi.success({
        title: t('bulkSuccessTitle'),
        description: t('bulkSuccessDescription', {
          count: response.body.deletedIds.length,
        }),
      });
      clearSelection();

      return true;
    }

    if (response.status === 409) {
      toastBulkConflict({ body: response.body, tooManyTitleKey: 'delete' });

      return false;
    }

    throw new Error(t('errorDescription'));
  }, [buildBulkFilterPayload, clearSelection, selection, t, toastBulkConflict]);
  const confirmDelete = useCallback(
    async (disposition: FindingDeleteSignalDisposition | null) => {
      setIsSubmitting(true);

      try {
        if (!deletingFindingId) {
          const didDelete = await deleteSelection();

          if (!didDelete) {
            setIsOpen(false);

            return;
          }
        }

        const singleResult = deletingFindingId
          ? await deleteSingle({ findingId: deletingFindingId, disposition })
          : { toastedFailure: false, changedSignal: false };

        closeDialog();
        await invalidateFindingById();

        if (singleResult.changedSignal && relatedSignal) {
          invalidateSignalQueriesAfterFindingStatusChange({
            queryClient,
            signalId: relatedSignal.id,
          });
        }

        await onMutated();
      } catch (error) {
        console.error('Failed to delete findings:', error);
        toasterApi.error({
          title: t('errorTitle'),
          description:
            error instanceof Error ? error.message : t('errorDescription'),
        });

        throw error;
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      closeDialog,
      deleteSelection,
      deleteSingle,
      deletingFindingId,
      invalidateFindingById,
      onMutated,
      queryClient,
      relatedSignal,
      t,
    ],
  );
  const openRelatedSignal = useCallback(
    (signalId: string) =>
      openInNewTab({
        id: `signal-${signalId}`,
        title: relatedSignal?.title || tCopilotTabTitles('signalDetails'),
        type: ENTITY_TAB_TYPE.SIGNAL,
        data: { signalId },
      }),
    [openInNewTab, relatedSignal?.title, tCopilotTabTitles],
  );

  return {
    isSubmitting,
    requestDeleteFinding,
    requestBulkDelete,
    dialogProps: {
      isOpen,
      isBulk: !deletingFindingId,
      relatedSignal,
      loading: isSubmitting,
      onOpenChange: ({ open }: { open: boolean }) => {
        if (open) {
          setIsOpen(true);
        } else {
          closeDialog();
        }
      },
      onOpenRelatedSignal: openRelatedSignal,
      onConfirm: confirmDelete,
    },
  };
}
